import CheckIcon from "@mui/icons-material/Check";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { postPdb, postWeb } from "../../api/ApiService";
import { routesConfig } from "../../config/routes";
import { useSummary } from "../../hooks/useSummary";
import { EpiId } from "../../shared/enums/LinkEpiId";
import { SavePdbType } from "../../shared/enums/SavePdbType";
import { SwitchViewMode } from "../../shared/enums/SwitchViewMode";
import { action } from "../../store/action";
import { useContactFormValues } from "../../store/hooks/useContactFormValues";
import { useEpiserverLink } from "../../store/hooks/useEpiServerLink";
import { useMachineTonnage } from "../../store/hooks/useMachineTonnage";
import { useMarket } from "../../store/hooks/useMarket";
import { CartOptional, CartProduct } from "../../store/interfaces/Cart";
import "./ContactUsForm.scss";
import { cookies } from "../../shared/helpers/cookies";

interface ContactUsFormProps {
  cartOptional: CartOptional;
  cartProduct: CartProduct;
}

interface Inputs {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  zip: string;
  country: string;
  location: string;
  message: string;
}

const formControlLabelStyle = {
  "& .MuiFormControlLabel-label": {
    width: "720px",
    "@media (max-width: 720px)": {
      width: "300px",
    },
  },
};

const nordicMarkets = ["sv-SE", "fi-FI", "nb-NO", "da-DK"];

const ContactUsForm = ({ cartOptional, cartProduct }: ContactUsFormProps) => {
  const dispatch = useDispatch();
  const contactFromValues = useContactFormValues();
  const [isNordicMarket, setIsNordicMarket] = useState<boolean>(false);
  const [isEnglishMarket, setIsEnglishMarket] = useState<boolean>(false);
  const [isConsentApproved, setIsConsentApproved] = useState<boolean>(false);
  const [isFormSent, setIsFormSent] = useState<boolean>(false);
  const [recieveNews, setRecieveNews] = useState<boolean>(false);
  const [isInit, setIsInit] = useState<boolean>(true);
  const [episerverUrl, setEpiserverUrl] = useState<string>(undefined);
  const history = useHistory<string>();
  const { t } = useTranslation("contactusform");
  const machineTonnage = useMachineTonnage();
  const episerverLinks = useEpiserverLink();
  const { getSummary } = useSummary();
  const {
    getValues,
    register,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  let selectedMarket = "";
  const flagMarket = cookies.getFlagMarket();
  const market = useMarket();
  if (flagMarket !== "") {
    selectedMarket = flagMarket;
  } else {
    selectedMarket = market;
  }
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const email = watch("email");
  const phone = watch("phone");
  const zip = watch("zip");
  const country = watch("country");
  const location = watch("location");

  useEffect(() => {
    const nordicMarket = nordicMarkets.includes(market);
    setIsNordicMarket(nordicMarket);

    const englishMarket = market === "en";
    setIsEnglishMarket(englishMarket);
  }, [market]);

  useEffect(() => {
    if (isInit) {
      reset(contactFromValues);
      setIsInit(false);
    }
  }, [contactFromValues, isInit, reset]);

  useEffect(() => {
    if (episerverLinks) {
      const url = `${process.env.REACT_APP_URLPATH}${episerverLinks[EpiId.PersonalDataPage]
        }`;
      setEpiserverUrl(url);
    }
  }, [episerverLinks]);

  const routeMarket = selectedMarket === "sv-SE" ? "sv" : selectedMarket;
  const routes = routesConfig[routeMarket];

  const redirectToThankYouStep = () => {
    history.push(routes.thankYouStep);
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    const productData = getSummary();
    const pdbBody = {
      firstName: data.firstName,
      surname: data.lastName,
      email: data.email,
      market: selectedMarket,
      type: SavePdbType.Offer,
      data: productData,
      postalnumber: data.zip,
      city: data.location,
      country: data.country,
      phonenumber: data.phone,
      gdpr: isConsentApproved,
      rotoad: recieveNews,
      message: data.message,
      created: new Date(),
    };

    setIsFormSent(true)
    postPdb("api/summary/", pdbBody).then((result) => {
      let pdbmarket = selectedMarket;
      if(selectedMarket === "fr-BE" || selectedMarket === "en-CA") {
        pdbmarket = "fr-FR";
      }
      if(selectedMarket === "nl-BE") {
        pdbmarket = "nl-NL";
      }
      const link = `${process.env.REACT_APP_URLPATH}${routesConfig[pdbmarket].contactUsStep}?id=${result.id}`;
      sendEmail(link, data);
      redirectToThankYouStep();
    });
  };

  const sendEmail = (link: string, data: Inputs) => {  
    const emailBody = {
      data: {
        link: link,
        language: selectedMarket,
        email: data.email,
        firstname: data.firstName,
        surname: data.lastName,
        city: data.location,
        country: data.country,
        postalnumber: data.zip,
        phonenumber: data.phone,
        gdpr: isConsentApproved,
        rotoad: recieveNews,
        message: data.message,
        machine:
          machineTonnage.switchViewMode === SwitchViewMode.MachineSearch &&
          machineTonnage.machineModel.name,
      },
    };

    postWeb("sendemail/", emailBody);
  };

  const onIntegrityChange = () => {
    setIsConsentApproved(!isConsentApproved);
  };

  const onRecieveNewsChange = () => {
    setRecieveNews(!recieveNews);
  };

  const onBlur = () => {
    const values = getValues();
    dispatch(action.saveContactUsForm(values));
  };

  return (
    <form className="form" onSubmit={handleSubmit(onSubmit)}>
      <div className="contactBoxContainer">
        <div className="formContainer">
          <div className="row">
            <div className="col-md-12 col-lg-6">
              <label htmlFor="firstName">{t("firstname").toUpperCase()}*</label>
              <TextField
                className="input"
                id="firstName"
                name="firstName"
                type="text"
                {...register("firstName", {
                  onBlur: onBlur,
                  required: t("firstnamemandatory"),
                  pattern: {
                    value: /^[^0-9]+$/i,
                    message: t("firstnameinvalid"),
                  },
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!errors.firstName && firstName ? (
                        <i>
                          <CheckIcon />
                        </i>
                      ) : (
                        ""
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <p>{errors.firstName?.message}</p>
            </div>
            <div className="col-md-12 col-lg-6">
              <label htmlFor="lastName">{t("lastname").toUpperCase()}*</label>
              <TextField
                className="input"
                id="lastName"
                name="lastName"
                type="text"
                {...register("lastName", {
                  onBlur: onBlur,
                  required: t("lastnamemandatory"),
                  pattern: {
                    value: /^[^0-9]+$/i,
                    message: t("lastnameinvalid"),
                  },
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!errors.lastName && lastName ? (
                        <i>
                          <CheckIcon />
                        </i>
                      ) : (
                        ""
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <p>{errors.lastName?.message}</p>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 col-lg-6">
              <label htmlFor="email">{t("email").toUpperCase()}*</label>
              <TextField
                className="input"
                id="email"
                name="email"
                type="text"
                {...register("email", {
                  onBlur: onBlur,
                  required: t("emailmandatory"),
                  pattern: {
                    value: /^[^@]+@[^@]{2,}\.[^@]{2,}$/,
                    message: t("emailinvalid"),
                  },
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!errors.email && email ? (
                        <i>
                          <CheckIcon />
                        </i>
                      ) : (
                        ""
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <p>{errors.email?.message}</p>
            </div>
            <div className="col-md-12 col-lg-6">
              <label htmlFor="phone">{t("phone").toUpperCase()}*</label>
              <TextField
                className="input"
                id="phone"
                name="phone"
                type="tel"
                {...register("phone", {
                  onBlur: onBlur,
                  required: t("phonemandatory"),
                  pattern: {
                    value: /^[\d\s\\(\\)\-+]+$/,
                    message: t("phoneinvalid"),
                  },
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!errors.phone && phone ? (
                        <i>
                          <CheckIcon />
                        </i>
                      ) : (
                        ""
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <p>{errors.phone?.message}</p>
            </div>
          </div>
          <div className="row">
            {isNordicMarket ? (
              <div className="col-md-12 col-lg-6">
                <label htmlFor="location">{t("city").toUpperCase()}*</label>
                <TextField
                  className="input"
                  id="location"
                  name="location"
                  type="text"
                  {...register("location", {
                    onBlur: onBlur,
                    required: t("city"),
                    pattern: {
                      value: /^[^0-9]+$/i,
                      message: t("locationinvalid"),
                    },
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {location?.length > 0 ? (
                          <i>
                            <CheckIcon />
                          </i>
                        ) : (
                          ""
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <p>{errors.location?.message}</p>
              </div>
            ) : (
              <div className="col-md-12 col-lg-6">
                <label htmlFor="zip">{t("zip").toUpperCase()}*</label>
                <TextField
                  className="input"
                  id="zip"
                  name="zip"
                  type="text"
                  {...register("zip", {
                    onBlur: onBlur,
                    required: t("zip"),
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {zip?.length > 0 ? (
                          <i>
                            <CheckIcon />
                          </i>
                        ) : (
                          ""
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <p>{errors.zip?.message}</p>
              </div>
            )}
            {isEnglishMarket && (
              <div className="col-md-12 col-lg-6">
                <label htmlFor="country">{t("country").toUpperCase()}*</label>
                <TextField
                  className="input"
                  id="country"
                  name="country"
                  type="text"
                  {...register("country", {
                    onBlur: onBlur,
                    required: t("countrymandatory"),
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {country?.length > 0 ? (
                          <i>
                            <CheckIcon />
                          </i>
                        ) : (
                          ""
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <p>{errors.country?.message}</p>
              </div>
            )}
          </div>
          <div className="row pt-4">
            <div className="col-md-12 col-lg-12">
              <label htmlFor="message">{t("message").toUpperCase()}</label>
              <TextField
                className="input"
                id="message"
                name="message"
                multiline
                type="text"
                size="medium"
                minRows={4}
                maxRows={4}
                {...register("message", {
                  onBlur: onBlur,
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end"></InputAdornment>
                  ),
                }}
              />
              <p>{errors.message?.message}</p>
            </div>
          </div>
          <div className="checkboxContainer">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isConsentApproved}
                    size="small"
                    onChange={onIntegrityChange}
                  />
                }
                label={
                  <>
                    <span>{t("checkboxintegrity")}</span>
                    <a
                      className="termsLink"
                      target="_blank"
                      rel="noreferrer"
                      href={episerverUrl}
                    >
                      {t("checkboxintegritylink")}
                    </a>
                  </>
                }
                labelPlacement="end"
                sx={formControlLabelStyle}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={recieveNews}
                    size="small"
                    onChange={onRecieveNewsChange}
                  />
                }
                label={`${t("checkboxnews")}`}
                labelPlacement="end"
                sx={formControlLabelStyle}
              />
            </FormGroup>
          </div>
          <div className="row">
            <div className="col-2">
              <div className="buttonPrimaryContainer">
                <Button
                  variant="contained"
                  type="submit"
                  className="button"
                  disabled={!isConsentApproved || isFormSent}
                >
                  {t("contactus").toUpperCase()}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ContactUsForm;
